import type { Question } from "../data/questions";

export interface QuestionStat {
  seen: number;
  correct: number;
  wrong: number;
  streak: number;
  lastSeen: number;
}

export interface ProgressData {
  version: 1;
  session: number;
  stats: Record<string, QuestionStat>;
  recent: string[];
}

const KEY = "sww-oma-progress-v1";
const empty = (): ProgressData => ({ version: 1, session: 0, stats: {}, recent: [] });

export function loadProgress(validQuestionIds?: Iterable<string>): ProgressData {
  try {
    const raw = localStorage.getItem(KEY);
    if (!raw) return empty();
    const data = JSON.parse(raw) as ProgressData;
    if (data.version !== 1 || !data.stats) return empty();
    if (!validQuestionIds) return data;

    const valid = new Set(validQuestionIds);
    const stats = Object.fromEntries(Object.entries(data.stats).filter(([id]) => valid.has(id)));
    const recent = data.recent.filter((id) => valid.has(id));
    const migrated = { ...data, stats, recent };
    if (Object.keys(stats).length !== Object.keys(data.stats).length || recent.length !== data.recent.length) saveProgress(migrated);
    return migrated;
  } catch {
    return empty();
  }
}

export function saveProgress(data: ProgressData): void {
  localStorage.setItem(KEY, JSON.stringify(data));
}

export function recordAnswer(data: ProgressData, question: Question, correct: boolean): ProgressData {
  const prior = data.stats[question.id] ?? { seen: 0, correct: 0, wrong: 0, streak: 0, lastSeen: 0 };
  const next: ProgressData = {
    ...data,
    stats: {
      ...data.stats,
      [question.id]: {
        seen: prior.seen + 1,
        correct: prior.correct + (correct ? 1 : 0),
        wrong: prior.wrong + (correct ? 0 : 1),
        streak: correct ? prior.streak + 1 : 0,
        lastSeen: Date.now(),
      },
    },
  };
  saveProgress(next);
  return next;
}

export function beginSession(data: ProgressData, ids: string[]): ProgressData {
  const next = { ...data, session: data.session + 1, recent: [...ids, ...data.recent].slice(0, 120) };
  saveProgress(next);
  return next;
}

export function progressSummary(data: ProgressData, total: number) {
  const values = Object.values(data.stats);
  const seen = values.filter((item) => item.seen > 0).length;
  const weak = values.filter((item) => item.wrong > 0 && item.streak < 3).length;
  const answers = values.reduce((sum, item) => sum + item.seen, 0);
  const correct = values.reduce((sum, item) => sum + item.correct, 0);
  return { seen, weak, answers, accuracy: answers ? Math.round((correct / answers) * 100) : 0, coverage: Math.round((seen / total) * 100) };
}

export function clearProgress(): ProgressData {
  localStorage.removeItem(KEY);
  return empty();
}
